/**
 * This is the only SPA boundary that may use "tenant": the API's current vocabulary
 * still uses tenant wire names, while app code above this file uses brand names.
 */

export type BrandKind = 'platform' | 'agency' | 'brand';
export type MembershipOrigin = 'direct' | 'inherited' | 'platform';
export type BrandRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface TenantWire {
  tenantId: string;
  slug: string;
  name: string;
  /** The API's current vocabulary: platform / agency / brand. */
  kind: BrandKind;
  /** The API's current vocabulary: parent tenant id for agency-owned brands. */
  parentTenantId: string | null;
  role: BrandRole;
  origin: MembershipOrigin;
  status: 'active' | 'invited' | 'disabled';
}

export interface Brand {
  brandId: string;
  slug: string;
  name: string;
  kind: BrandKind;
  parentBrandId: string | null;
  role: BrandRole;
  origin: MembershipOrigin;
  status: 'active' | 'invited' | 'disabled';
}

export interface MeWire {
  user: {
    id: string;
    email: string;
    name: string | null;
    status: string;
    hasPassword: boolean;
  } | null;
  activeTenantId: string | null;
  activeRole: BrandRole | null;
  tenants: TenantWire[];
}

export interface Me {
  user: MeWire['user'];
  activeBrandId: string | null;
  activeRole: BrandRole | null;
  brands: Brand[];
}

export interface RefreshWire {
  accessToken: string;
  activeTenantId: string | null;
  activeRole: string | null;
}

export interface HostnameBrandWire {
  tenant: { id: string; slug: string; name: string } | null;
}

export interface HostnameBrand {
  brand: { brandId: string; slug: string; name: string } | null;
}

export interface BrandSummaryWire {
  id: string;
  slug: string;
  name: string;
  status: string;
  billingPlan?: { planId: string | null; tier: string | null; hasSubscription: boolean };
  memberCount?: number;
  /**
   * Parsed on the wire, NOT a JSON string. It was typed here as
   * `settingsJson?: string` — a field the API has never sent — so every page
   * that read it got undefined and TypeScript agreed with them. Two did, and
   * both ended up believing every brand has zero regions.
   */
  settings?: { regions?: string[]; leads_regions?: string[] };
}

export type BrandSummary = BrandSummaryWire;

export function planBadgeLabel(badge?: BrandSummaryWire['billingPlan']): string {
  if (!badge?.hasSubscription) return 'No subscription';
  if (!badge.tier) return 'No subscription';
  const known: Record<string, string> = {
    pro: 'Pro',
    enterprise: 'Enterprise',
    starter: 'Starter',
    trial: 'Trial',
  };
  return known[badge.tier] ?? badge.tier;
}

export interface SignupRowWire {
  id: string;
  email: string;
  name: string | null;
  requestedTenantSlug: string | null;
  reason: string | null;
  status: 'email_unverified' | 'pending_review';
  createdAt: number;
}

export interface SignupRow {
  id: string;
  email: string;
  name: string | null;
  requestedBrandSlug: string | null;
  reason: string | null;
  status: 'email_unverified' | 'pending_review';
  createdAt: number;
}

export interface AgencyTileWire {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: string;
  status: string;
  region: string | null;
  snapshot: {
    fetchedAt: number;
    lastDate: string | null;
    totals: { Cost: number; Leads: number; Clicks: number; CPL: number; CTR?: number } | null;
  } | null;
}

export interface AgencyBrandTile {
  brandId: string;
  brandName: string;
  brandSlug: string;
  role: string;
  status: string;
  region: string | null;
  snapshot: AgencyTileWire['snapshot'];
}

export interface AgencyOverviewWire {
  rollup: {
    cost: number;
    leads: number;
    clicks: number;
    cpl: number;
    tenantsWithData: number;
  };
  tenants: AgencyTileWire[];
}

export interface AgencyOverview {
  rollup: {
    cost: number;
    leads: number;
    clicks: number;
    cpl: number;
    brandsWithData: number;
  };
  brands: AgencyBrandTile[];
}

export interface DashboardListWire<TDashboard> {
  tenantId: string;
  tenantName: string;
  regions: string[];
  dashboards: TDashboard[];
}

export interface DashboardList<TDashboard> {
  brandId: string;
  brandName: string;
  regions: string[];
  dashboards: TDashboard[];
}

export interface DashboardEnvelopeWire<TLayout> {
  dashboard: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    icon: string | null;
    defaultCountry: string | null;
    defaultDateRange: string;
    version: number;
    publishedAt: number;
  };
  tenantId: string;
  tenantName: string;
  regions: string[];
  layout: TLayout;
}

export interface DashboardEnvelope<TLayout> {
  dashboard: DashboardEnvelopeWire<TLayout>['dashboard'];
  brandId: string;
  brandName: string;
  regions: string[];
  layout: TLayout;
}

export interface SearchResultWire {
  tenants: Array<{ id: string; slug: string; name: string; status: string }>;
  dashboards: Array<{ id: string; tenantId: string; slug: string; name: string }>;
  recentAudit: Array<{ id: string; action: string; tenantId: string | null; at: number }>;
}

export interface SearchResult {
  brands: Array<{ id: string; slug: string; name: string; status: string }>;
  dashboards: Array<{ id: string; brandId: string; slug: string; name: string }>;
  recentAudit: Array<{ id: string; action: string; brandId: string | null; at: number }>;
}

export interface AuditRowWire {
  id: string;
  at: number;
  action: string;
  entity: string | null;
  entityId: string | null;
  tenantId: string | null;
  userId: string | null;
  ip: string | null;
  ua: string | null;
  beforeJson: string | null;
  afterJson: string | null;
}

export type AuditRow = Omit<AuditRowWire, 'tenantId'> & { brandId: string | null };

export interface SupportTicketWire {
  id: string;
  email: string;
  name: string | null;
  topic: string;
  body: string;
  context_json: string;
  tenant_id: string | null;
  user_id: string | null;
  status: 'open' | 'in_progress' | 'resolved';
  created_at: number;
  resolved_at: number | null;
  resolved_by: string | null;
  resolution_notes: string | null;
}

export type SupportTicket = Omit<SupportTicketWire, 'tenant_id'> & { brandId: string | null };

export interface UsageReportWire {
  asOf: string;
  tenants: { total: number; active: number; trial: number };
  users: { total: number; active7d: number; active30d: number };
  signups: { openQueue: number; approved30d: number };
  dashboards: { total: number; published: number };
  reports30d: { sent: number; failed: number };
  alerts30d: { fired: number };
  embeds: { totalViews: number };
  sync: { lastRunAt: number | null; lastSucceeded: number | null; lastFailed: number | null };
}

export type UsageReport = Omit<UsageReportWire, 'tenants'> & {
  brands: UsageReportWire['tenants'];
};

export interface SyncFailureWire {
  tenantId: string;
  dataSourceId: string;
  kind: string;
  country: string;
  errorMessage: string | null;
  fetchedAt: number;
}

export type SyncFailure = Omit<SyncFailureWire, 'tenantId'> & { brandId: string };

export function toBrand(wire: TenantWire): Brand {
  return {
    brandId: wire.tenantId,
    slug: wire.slug,
    name: wire.name,
    kind: wire.kind,
    parentBrandId: wire.parentTenantId,
    role: wire.role,
    origin: wire.origin,
    status: wire.status,
  };
}

export function toMe(wire: MeWire): Me {
  return {
    user: wire?.user ?? null,
    activeBrandId: wire?.activeTenantId ?? null,
    activeRole: wire?.activeRole ?? null,
    // Not `wire.tenants.map(...)`: this boundary exists because the wire shape
    // is expected to change under us, and an absent list must degrade to an
    // empty one rather than crash AuthProvider at boot with a TypeError.
    brands: Array.isArray(wire?.tenants) ? wire.tenants.map(toBrand) : [],
  };
}

export function toHostnameBrand(wire: HostnameBrandWire): HostnameBrand {
  return {
    brand: wire.tenant
      ? { brandId: wire.tenant.id, slug: wire.tenant.slug, name: wire.tenant.name }
      : null,
  };
}

export function toSignupRow(wire: SignupRowWire): SignupRow {
  return {
    id: wire.id,
    email: wire.email,
    name: wire.name,
    requestedBrandSlug: wire.requestedTenantSlug,
    reason: wire.reason,
    status: wire.status,
    createdAt: wire.createdAt,
  };
}

export function toAgencyOverview(wire: AgencyOverviewWire): AgencyOverview {
  return {
    rollup: {
      cost: wire.rollup.cost,
      leads: wire.rollup.leads,
      clicks: wire.rollup.clicks,
      cpl: wire.rollup.cpl,
      brandsWithData: wire.rollup.tenantsWithData,
    },
    brands: wire.tenants.map((tile) => ({
      brandId: tile.tenantId,
      brandName: tile.tenantName,
      brandSlug: tile.tenantSlug,
      role: tile.role,
      status: tile.status,
      region: tile.region,
      snapshot: tile.snapshot,
    })),
  };
}

export function toDashboardList<TDashboard>(
  wire: DashboardListWire<TDashboard>,
): DashboardList<TDashboard> {
  return {
    brandId: wire.tenantId,
    brandName: wire.tenantName,
    regions: wire.regions,
    dashboards: wire.dashboards,
  };
}

export function toDashboardEnvelope<TLayout>(
  wire: DashboardEnvelopeWire<TLayout>,
): DashboardEnvelope<TLayout> {
  return {
    dashboard: wire.dashboard,
    brandId: wire.tenantId,
    brandName: wire.tenantName,
    regions: wire.regions,
    layout: wire.layout,
  };
}

export function toSearchResult(wire: SearchResultWire): SearchResult {
  return {
    brands: wire.tenants,
    dashboards: wire.dashboards.map((dashboard) => ({
      id: dashboard.id,
      brandId: dashboard.tenantId,
      slug: dashboard.slug,
      name: dashboard.name,
    })),
    recentAudit: wire.recentAudit.map((row) => ({
      id: row.id,
      action: row.action,
      brandId: row.tenantId,
      at: row.at,
    })),
  };
}

export function toAuditRow(wire: AuditRowWire): AuditRow {
  const { tenantId, ...rest } = wire;
  return { ...rest, brandId: tenantId };
}

export function toSupportTicket(wire: SupportTicketWire): SupportTicket {
  const { tenant_id: brandId, ...rest } = wire;
  return { ...rest, brandId };
}

export function toUsageReport(wire: UsageReportWire): UsageReport {
  const { tenants, ...rest } = wire;
  return { ...rest, brands: tenants };
}

export function toSyncFailure(wire: SyncFailureWire): SyncFailure {
  const { tenantId, ...rest } = wire;
  return { ...rest, brandId: tenantId };
}

export function toBrandSummaries<T extends BrandSummaryWire = BrandSummaryWire>(wire: {
  tenants: T[];
}): T[] {
  return Array.isArray(wire?.tenants) ? wire.tenants : [];
}

/**
 * Returns a null brandId rather than throwing when the response has no tenant.
 * The onboarding wizard already guards every later step on `createdBrandId`, so
 * null stalls the wizard where it is; the unchecked traversal this replaces
 * either threw a TypeError or fed "undefined" into the next request's URL.
 */
export function toCreatedBrand(wire: unknown): { brand: { brandId: string | null } } {
  const id = (wire as { tenant?: { id?: string } } | null | undefined)?.tenant?.id;
  return { brand: { brandId: typeof id === 'string' ? id : null } };
}

export function toBrandDetail<T>(wire: unknown): { brand: T | null } {
  const brand = (wire as { tenant?: T } | null | undefined)?.tenant;
  return { brand: brand ?? null };
}

export const PLATFORM_BRAND_ID = 'tnt_platform';
export const ADMIN_BRANDS_ROUTE = '/admin/tenants';
export const ADMIN_BRANDS_ROUTE_SEGMENT = 'tenants';
export const ADMIN_BRAND_DETAIL_ROUTE_SEGMENT = 'tenants/:id';
export const ADMIN_BRAND_URL_PARAM = 'tenant';

export function adminBrandRoute(brandId?: string): string {
  return brandId ? `${ADMIN_BRANDS_ROUTE}/${brandId}` : ADMIN_BRANDS_ROUTE;
}

export function adminBrandsApiPath(brandId?: string): string {
  return brandId ? `/admin/tenants/${brandId}` : '/admin/tenants';
}

export function adminBrandChildApiPath(brandId: string, childPath: string): string {
  return `/admin/tenants/${brandId}/${childPath}`;
}

export function toWirePath(path: string): string {
  if (path === '/admin/brands') return '/admin/tenants';
  if (path.startsWith('/admin/brands/'))
    return `/admin/tenants/${path.slice('/admin/brands/'.length)}`;
  return path;
}

export function adminSignupsApiPath(signupId?: string): string {
  return signupId ? `/admin/signups/${signupId}` : '/admin/signups';
}

export function adminAuditApiPath(query = ''): string {
  return `/admin/audit${query ? `?${query}` : ''}`;
}

export function adminSearchApiPath(query: string): string {
  return `/admin/search${query ? `?q=${encodeURIComponent(query)}` : ''}`;
}

export function adminSyncRunsApiPath(limit: number): string {
  return `/admin/sync/runs?limit=${limit}`;
}

export const ADMIN_SYNC_RUN_API_PATH = '/admin/sync/run';
export const ANALYTICS_DASHBOARDS_API_PATH = '/analytics/dashboards';
export const AGENCY_OVERVIEW_API_PATH = '/agency/overview';
export const HOSTNAME_LOOKUP_API_PATH = '/tenant-by-hostname';
export const AUTH_SWITCH_BRAND_API_PATH = '/auth/switch-tenant';
export const ERROR_CODE_BRAND_NOT_FOUND = 'tenant_not_found';
export const ERROR_CODE_REGION_NOT_IN_BRAND = 'region_not_in_tenant';
export const ERROR_CODE_NO_ACTIVE_BRAND = 'no_active_tenant';
export const ERROR_CODE_BRAND_NOT_CREATED_YET = 'tenant_not_created_yet';
export const ADMIN_BRAND_ACTION_PREFIX_PLACEHOLDER = 'platform.tenant.';
export const NAMING_DEFAULT_BRAND_SEGMENT_KEY = 'tenant';

// The API reads `tenant_id` from the query string; the SPA holds a brand id.
// Used by /admin/audit and by /oauth/connections + /oauth/*/authorize — those
// three hand-rolled `brand_id=` instead, which the API has never read anywhere,
// so the ad-platform section refused every load with `tenant_id_required` and
// showed "Please choose a workspace first." beside a chosen workspace.
export function brandSnakeQueryParam(brandId: string): string {
  return `tenant_id=${encodeURIComponent(brandId)}`;
}

// /analytics and legacy dashboard endpoints expect the API's camelCase query parameter.
export function brandCamelQueryParam(brandId: string): string {
  return `tenantId=${encodeURIComponent(brandId)}`;
}

// /auth/switch-tenant expects the API's tenantId request body.
export function switchBrandBody(brandId: string): unknown {
  return { tenantId: brandId };
}

// /admin/signups PATCH expects tenantSlug and tenantName when approving.
export function signupDecisionBody(input: {
  action: 'approve' | 'reject';
  notes?: string;
  brandSlug?: string;
  brandName?: string;
  role?: BrandRole;
}): unknown {
  return {
    action: input.action,
    notes: input.notes,
    ...(input.action === 'approve'
      ? {
          tenantSlug: input.brandSlug,
          tenantName: input.brandName,
          role: input.role,
        }
      : {}),
  };
}

// Admin overview maps the API's current audit action vocabulary to user-facing labels.
export const ADMIN_ACTION_LABELS: Record<string, string> = {
  'platform.tenant.create': 'Workspace created',
  'platform.tenant.update': 'Workspace updated',
  'platform.tenant.delete': 'Workspace archived',
  'platform.tenant.invite': 'Member invited',
  'platform.tenant.member.remove': 'Member removed',
  'platform.dashboard.publish': 'Dashboard published',
  'platform.dashboard.rollback': 'Dashboard rolled back',
  'platform.naming.publish': 'Naming convention published',
  'platform.data_source.create': 'Data source connected',
  'platform.data_source.update': 'Data source updated',
  'platform.data_source.delete': 'Data source removed',
  'platform.document.upload': 'Document uploaded',
  'platform.document.delete': 'Document removed',
  'platform.domain.create': 'Domain attached',
  'platform.domain.delete': 'Domain removed',
  'signup.approve': 'Signup approved',
  'signup.reject': 'Signup rejected',
  'auth.password.set': 'Password set',
  'auth.password.change': 'Password changed',
};
